export const STORAGE_KEY = 'gbm_studio_demo_draft_v1';
export const FORMAL_STORAGE_KEY = 'gbm_studio_formal_draft_v1';
export const STORAGE_VERSION = 1;
export const DRAFT_LOCK_NAME = 'gbm_studio_demo_draft_lock_v1';
export const MAX_INLINE_ITEMS = 256;
export const MAX_ATTACHMENTS = 12;
export const MAX_GIFT_MESSAGE = 240;

export type InlineItem = { uid: string; component_id: string; orientation_deg: number };
export type Attachment = { uid: string; component_id: string; anchor_uid: string; orientation_deg: number };
export type WristUnit = 'in' | 'cm';
export type FitPreference = 'snug' | 'comfort' | 'loose';
export type PackagingOption = 'standard' | 'gift_box';
export type WristInput = { value: string; unit: WristUnit };
export type WristState = { normalized_um: number; confirmed: boolean };
export type RecipientMode = 'self' | 'gift';
export type RecommendationContext = {
  schema_version: '1.0';
  quiz_version: 'gbm-5q-v1';
  mapping_version: string;
  template_id: string;
  template_version: string;
  theme_code: 'protection' | 'peace-and-blessings' | 'abundance' | 'calm' | 'zodiac';
  occasion_code: 'everyday' | 'new_beginning' | 'special_date' | 'milestone' | 'just_because' | null;
  color_code: 'soft-light' | 'deep-rich' | 'warm-golden' | 'cool-luminous' | null;
  style_code: 'delicate' | 'balanced' | 'bold' | null;
};

export type Design = {
  schema_version: '1.0';
  recipient_mode: RecipientMode;
  intentions: string[];
  zodiac_sign: null;
  birth_month: null;
  occasion_code: 'none';
  inline_items: InlineItem[];
  attachments: Attachment[];
  wrist: WristInput | null;
  fit_preference: FitPreference | null;
  packaging_option_id: PackagingOption | null;
  private_message_ref: null;
  recommendation_context?: RecommendationContext;
};

export type Snapshot = { design: Design; selectedInlineUid: string | null; nextUid: number; wristState: WristState | null; giftMessage: string };
export type ServerDesignContent = {
  design_id: string;
  design_version: number;
  design: Design;
  wrist_state: WristState | null;
  gift_message: string;
};
export type ServerDesignIdentity = { designId: string | null; designVersion: number };
export type History = { past: Snapshot[]; present: Snapshot; future: Snapshot[] };
export type OperationResult = { snapshot: Snapshot; error?: string };
export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
export type DraftLockManager = {
  request<T>(name: string, options: { mode: 'exclusive' }, callback: () => T | Promise<T>): Promise<T>;
};
export type DraftLoadResult =
  | { status: 'empty'; snapshot: Snapshot; revision: 0; writerId: null }
  | { status: 'loaded'; snapshot: Snapshot; savedAt: string; revision: number; writerId: string | null }
  | { status: 'invalid'; snapshot: Snapshot; message: string; revision: null; writerId: null }
  | { status: 'unavailable'; snapshot: Snapshot; message: string; revision: null; writerId: null };
export type DraftWriteResult =
  | { ok: true; savedAt: string; revision: number }
  | { ok: false; kind: 'conflict' | 'invalid' | 'unavailable'; message: string; actualRevision: number | null };
export type DraftClearResult =
  | { ok: true; savedAt: string; revision: number }
  | { ok: false; kind: 'conflict' | 'unavailable'; message: string; actualRevision: number | null };
export type ReviewWristEditor = {
  value: string;
  unit: WristUnit;
  fitPreference: FitPreference | null;
  hasUnconfirmedChanges: boolean;
};
export type ReviewPreview = {
  snapshot: Snapshot;
  componentSummary: Array<{ componentId: string; count: number }>;
  attachmentAnchors: Array<{
    uid: string;
    componentId: string;
    anchorUid: string;
    anchorPosition: number | null;
  }>;
  wrist:
    | { status: 'confirmed'; value: string; unit: WristUnit; fitPreference: FitPreference; normalizedUm: number }
    | { status: 'pending'; value: string; unit: WristUnit; fitPreference: FitPreference | null }
    | { status: 'not_provided' };
};

const UID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const COMPONENT_PATTERN = /^[A-Za-z0-9_-]{1,80}$/;
const WRIST_PATTERN = /^([0-9]{1,3})(?:\.([0-9]{1,3}))?$/;
const WRITER_PATTERN = /^[A-Za-z0-9_-]{8,80}$/;

export function cloneSnapshot(snapshot: Snapshot): Snapshot {
  return {
    design: {
      ...snapshot.design,
      intentions: [...snapshot.design.intentions],
      inline_items: snapshot.design.inline_items.map((item) => ({ ...item })),
      attachments: snapshot.design.attachments.map((item) => ({ ...item })),
      wrist: snapshot.design.wrist ? { ...snapshot.design.wrist } : null,
      ...(snapshot.design.recommendation_context
        ? { recommendation_context: { ...snapshot.design.recommendation_context } }
        : {}),
    },
    selectedInlineUid: snapshot.selectedInlineUid,
    nextUid: snapshot.nextUid,
    wristState: snapshot.wristState ? { ...snapshot.wristState } : null,
    giftMessage: snapshot.giftMessage ?? '',
  };
}

export function createEmptySnapshot(): Snapshot {
  return {
    design: {
      schema_version: '1.0', recipient_mode: 'self', intentions: [], zodiac_sign: null,
      birth_month: null, occasion_code: 'none', inline_items: [], attachments: [], wrist: null,
      fit_preference: null, packaging_option_id: null, private_message_ref: null,
    },
    selectedInlineUid: null,
    nextUid: 1,
    wristState: null,
    giftMessage: '',
  };
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, child]) => [key, canonicalize(child)]));
  }
  return value;
}

export function serverDesignMatchesSnapshot(snapshot: Snapshot, saved: ServerDesignContent): boolean {
  const local = { design: snapshot.design, wrist_state: snapshot.wristState, gift_message: snapshot.giftMessage };
  const remote = { design: saved.design, wrist_state: saved.wrist_state, gift_message: saved.gift_message };
  return JSON.stringify(canonicalize(local)) === JSON.stringify(canonicalize(remote));
}

/** Discovering a newer server record must not advance the write baseline of older local content. */
export function resolveServerDesignDiscovery(
  snapshot: Snapshot,
  current: ServerDesignIdentity,
  saved: ServerDesignContent,
): { identity: ServerDesignIdentity; adopted: boolean } {
  if (!serverDesignMatchesSnapshot(snapshot, saved)) return { identity: current, adopted: false };
  return {
    identity: { designId: saved.design_id, designVersion: saved.design_version },
    adopted: current.designId !== saved.design_id || current.designVersion !== saved.design_version,
  };
}

export function createReviewPreview(snapshot: Snapshot, wristEditor: ReviewWristEditor): ReviewPreview {
  const reviewSnapshot = cloneSnapshot(snapshot);
  const counts = new Map<string, number>();
  for (const item of reviewSnapshot.design.inline_items) {
    counts.set(item.component_id, (counts.get(item.component_id) ?? 0) + 1);
  }
  const attachmentAnchors = reviewSnapshot.design.attachments.map((attachment) => {
    const index = reviewSnapshot.design.inline_items.findIndex((item) => item.uid === attachment.anchor_uid);
    return {
      uid: attachment.uid,
      componentId: attachment.component_id,
      anchorUid: attachment.anchor_uid,
      anchorPosition: index >= 0 ? index + 1 : null,
    };
  });
  let wrist: ReviewPreview['wrist'];
  if (wristEditor.hasUnconfirmedChanges || (reviewSnapshot.design.wrist !== null && !reviewSnapshot.wristState?.confirmed)) {
    wrist = {
      status: 'pending',
      value: wristEditor.value,
      unit: wristEditor.unit,
      fitPreference: wristEditor.fitPreference,
    };
  } else if (reviewSnapshot.design.wrist && reviewSnapshot.design.fit_preference && reviewSnapshot.wristState?.confirmed) {
    wrist = {
      status: 'confirmed',
      value: reviewSnapshot.design.wrist.value,
      unit: reviewSnapshot.design.wrist.unit,
      fitPreference: reviewSnapshot.design.fit_preference,
      normalizedUm: reviewSnapshot.wristState.normalized_um,
    };
  } else {
    wrist = { status: 'not_provided' };
  }
  return {
    snapshot: reviewSnapshot,
    componentSummary: [...counts].map(([componentId, count]) => ({ componentId, count })),
    attachmentAnchors,
    wrist,
  };
}

export function createHistory(snapshot = createEmptySnapshot()): History {
  return { past: [], present: cloneSnapshot(snapshot), future: [] };
}

export function commit(history: History, snapshot: Snapshot): History {
  return { past: [...history.past.slice(-99), cloneSnapshot(history.present)], present: cloneSnapshot(snapshot), future: [] };
}

export function undo(history: History): History {
  const previous = history.past.at(-1);
  if (!previous) return history;
  return { past: history.past.slice(0, -1), present: cloneSnapshot(previous), future: [cloneSnapshot(history.present), ...history.future] };
}

export function redo(history: History): History {
  const next = history.future[0];
  if (!next) return history;
  return { past: [...history.past, cloneSnapshot(history.present)], present: cloneSnapshot(next), future: history.future.slice(1) };
}

function makeUid(snapshot: Snapshot, prefix: 'bead' | 'charm'): [string, number] | null {
  const used = new Set([
    ...snapshot.design.inline_items.map((item) => item.uid),
    ...snapshot.design.attachments.map((item) => item.uid),
  ]);
  let sequence = snapshot.nextUid;
  while (Number.isSafeInteger(sequence) && sequence > 0) {
    const uid = `${prefix}-${sequence.toString(36)}`;
    if (!used.has(uid)) {
      if (sequence === Number.MAX_SAFE_INTEGER) return null;
      return [uid, sequence + 1];
    }
    if (sequence === Number.MAX_SAFE_INTEGER) return null;
    sequence += 1;
  }
  return null;
}

export function addInline(snapshot: Snapshot, componentId: string): OperationResult {
  if (snapshot.design.inline_items.length >= MAX_INLINE_ITEMS) return { snapshot, error: `This demo supports up to ${MAX_INLINE_ITEMS} inline pieces.` };
  const allocation = makeUid(snapshot, 'bead');
  if (!allocation) return { snapshot, error: 'A unique piece ID could not be allocated. Reset this damaged draft before continuing.' };
  const [uid, nextUid] = allocation;
  const next = cloneSnapshot(snapshot);
  next.design.inline_items.push({ uid, component_id: componentId, orientation_deg: 0 });
  next.selectedInlineUid = uid;
  next.nextUid = nextUid;
  return { snapshot: next };
}

export function replaceSelected(snapshot: Snapshot, componentId: string): OperationResult {
  if (!snapshot.selectedInlineUid) return { snapshot, error: 'Select a piece before replacing it.' };
  if (!snapshot.design.inline_items.some((item) => item.uid === snapshot.selectedInlineUid)) return { snapshot, error: 'The selected piece is no longer in the design.' };
  const next = cloneSnapshot(snapshot);
  next.design.inline_items = next.design.inline_items.map((item) => item.uid === snapshot.selectedInlineUid ? { ...item, component_id: componentId } : item);
  return { snapshot: next };
}

export function moveInline(snapshot: Snapshot, uid: string, direction: -1 | 1): OperationResult {
  const index = snapshot.design.inline_items.findIndex((item) => item.uid === uid);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= snapshot.design.inline_items.length) return { snapshot };
  const next = cloneSnapshot(snapshot);
  [next.design.inline_items[index], next.design.inline_items[target]] = [next.design.inline_items[target], next.design.inline_items[index]];
  return { snapshot: next };
}

export function removeInline(snapshot: Snapshot, uid: string): OperationResult & { removedAttachments: number } {
  const removedAttachments = snapshot.design.attachments.filter((item) => item.anchor_uid === uid).length;
  const next = cloneSnapshot(snapshot);
  next.design.inline_items = next.design.inline_items.filter((item) => item.uid !== uid);
  next.design.attachments = next.design.attachments.filter((item) => item.anchor_uid !== uid);
  if (next.selectedInlineUid === uid) next.selectedInlineUid = next.design.inline_items[0]?.uid ?? null;
  return { snapshot: next, removedAttachments };
}

export function addAttachment(snapshot: Snapshot, componentId: string, anchorUid: string): OperationResult {
  if (snapshot.design.attachments.length >= MAX_ATTACHMENTS) return { snapshot, error: `This demo supports up to ${MAX_ATTACHMENTS} attachments.` };
  if (!snapshot.design.inline_items.some((item) => item.uid === anchorUid)) return { snapshot, error: 'Choose an inline piece before adding an attachment.' };
  const allocation = makeUid(snapshot, 'charm');
  if (!allocation) return { snapshot, error: 'A unique attachment ID could not be allocated. Reset this damaged draft before continuing.' };
  const [uid, nextUid] = allocation;
  const next = cloneSnapshot(snapshot);
  next.design.attachments.push({ uid, component_id: componentId, anchor_uid: anchorUid, orientation_deg: 0 });
  next.nextUid = nextUid;
  return { snapshot: next };
}

export function moveAttachmentAnchor(snapshot: Snapshot, attachmentUid: string, anchorUid: string): OperationResult {
  if (!snapshot.design.inline_items.some((item) => item.uid === anchorUid)) return { snapshot, error: 'That anchor is not in this design.' };
  const next = cloneSnapshot(snapshot);
  let found = false;
  next.design.attachments = next.design.attachments.map((item) => {
    if (item.uid !== attachmentUid) return item;
    found = true;
    return { ...item, anchor_uid: anchorUid };
  });
  return found ? { snapshot: next } : { snapshot, error: 'That attachment is no longer in the design.' };
}

export function removeAttachment(snapshot: Snapshot, attachmentUid: string): OperationResult {
  const next = cloneSnapshot(snapshot);
  next.design.attachments = next.design.attachments.filter((item) => item.uid !== attachmentUid);
  return { snapshot: next };
}

export type WristParseResult =
  | { ok: true; normalizedUm: number; value: string; unit: WristUnit }
  | { ok: false; message: string };

export function parseWristToUm(rawValue: string, unit: WristUnit): WristParseResult {
  const value = rawValue.trim();
  if (value === '') return { ok: false, message: 'Enter a wrist measurement, or choose “Skip for now”.' };
  const match = WRIST_PATTERN.exec(value);
  if (!match) return { ok: false, message: 'Use a positive number with up to three digits before and after the decimal point.' };
  const whole = BigInt(match[1]);
  const fraction = BigInt((match[2] ?? '').padEnd(3, '0'));
  const thousandths = (whole * 1000n) + fraction;
  if (thousandths <= 0n) return { ok: false, message: 'Wrist measurement must be greater than zero.' };
  const factor = unit === 'in' ? 25400n : 10000n;
  const numerator = thousandths * factor;
  if (numerator % 1000n !== 0n) {
    return { ok: false, message: 'That value is more precise than whole micrometres allow. Use fewer decimal places.' };
  }
  const normalized = numerator / 1000n;
  if (normalized > BigInt(Number.MAX_SAFE_INTEGER)) {
    return { ok: false, message: 'That measurement is too large to use here. Check the number or choose “Skip for now”.' };
  }
  return { ok: true, normalizedUm: Number(normalized), value, unit };
}

export function formatWristUm(normalizedUm: number, unit: WristUnit): string {
  const divisor = unit === 'in' ? 25400 : 10000;
  return (normalizedUm / divisor).toFixed(3).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
}

export function confirmWrist(
  snapshot: Snapshot,
  rawValue: string,
  unit: WristUnit,
  fitPreference: FitPreference | null,
): OperationResult {
  if (!fitPreference) return { snapshot, error: 'Choose a wear preference before confirming the measurement.' };
  const parsed = parseWristToUm(rawValue, unit);
  if (!parsed.ok) return { snapshot, error: parsed.message };
  const next = cloneSnapshot(snapshot);
  next.design.wrist = { value: parsed.value, unit: parsed.unit };
  next.design.fit_preference = fitPreference;
  next.wristState = { normalized_um: parsed.normalizedUm, confirmed: true };
  return { snapshot: next };
}

export function confirmWristPreference(snapshot: Snapshot, fitPreference: FitPreference | null): OperationResult {
  if (!fitPreference) return { snapshot, error: 'Choose a wear preference before confirming the measurement.' };
  if (!snapshot.design.wrist || !snapshot.wristState) {
    return { snapshot, error: 'Enter and confirm a wrist measurement before updating the wear preference.' };
  }
  const next = cloneSnapshot(snapshot);
  next.design.fit_preference = fitPreference;
  next.wristState = { ...next.wristState!, confirmed: true };
  return { snapshot: next };
}

export function invalidateWristConfirmation(snapshot: Snapshot): OperationResult {
  if (!snapshot.wristState?.confirmed) return { snapshot };
  const next = cloneSnapshot(snapshot);
  next.wristState = { ...next.wristState!, confirmed: false };
  return { snapshot: next };
}

export function clearWrist(snapshot: Snapshot): OperationResult {
  const next = cloneSnapshot(snapshot);
  next.design.wrist = null;
  next.design.fit_preference = null;
  next.wristState = null;
  return { snapshot: next };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function validInline(value: unknown, allowed: ReadonlySet<string>): value is InlineItem {
  if (!isObject(value) || !hasExactKeys(value, ['uid', 'component_id', 'orientation_deg'])) return false;
  return typeof value.uid === 'string' && UID_PATTERN.test(value.uid)
    && typeof value.component_id === 'string' && COMPONENT_PATTERN.test(value.component_id) && allowed.has(value.component_id)
    && Number.isInteger(value.orientation_deg) && Number(value.orientation_deg) >= 0 && Number(value.orientation_deg) <= 359;
}

function validAttachment(value: unknown, allowed: ReadonlySet<string>): value is Attachment {
  if (!isObject(value) || !hasExactKeys(value, ['uid', 'component_id', 'anchor_uid', 'orientation_deg'])) return false;
  return typeof value.uid === 'string' && UID_PATTERN.test(value.uid)
    && typeof value.component_id === 'string' && COMPONENT_PATTERN.test(value.component_id) && allowed.has(value.component_id)
    && typeof value.anchor_uid === 'string' && UID_PATTERN.test(value.anchor_uid)
    && Number.isInteger(value.orientation_deg) && Number(value.orientation_deg) >= 0 && Number(value.orientation_deg) <= 359;
}

function validWrist(value: unknown): value is WristInput {
  if (!isObject(value) || !hasExactKeys(value, ['value', 'unit'])) return false;
  return typeof value.value === 'string' && WRIST_PATTERN.test(value.value)
    && (value.unit === 'in' || value.unit === 'cm');
}

function validFitPreference(value: unknown): value is FitPreference | null {
  return value === null || value === 'snug' || value === 'comfort' || value === 'loose';
}

function validRecommendationContext(value: unknown): value is RecommendationContext {
  if (!isObject(value) || !hasExactKeys(value, [
    'schema_version', 'quiz_version', 'mapping_version', 'template_id', 'template_version',
    'theme_code', 'occasion_code', 'color_code', 'style_code',
  ])) return false;
  return value.schema_version === '1.0'
    && value.quiz_version === 'gbm-5q-v1'
    && typeof value.mapping_version === 'string' && COMPONENT_PATTERN.test(value.mapping_version)
    && typeof value.template_id === 'string' && COMPONENT_PATTERN.test(value.template_id)
    && typeof value.template_version === 'string' && COMPONENT_PATTERN.test(value.template_version)
    && ['protection', 'peace-and-blessings', 'abundance', 'calm', 'zodiac'].includes(String(value.theme_code))
    && (value.occasion_code === null || ['everyday', 'new_beginning', 'special_date', 'milestone', 'just_because'].includes(String(value.occasion_code)))
    && (value.color_code === null || ['soft-light', 'deep-rich', 'warm-golden', 'cool-luminous'].includes(String(value.color_code)))
    && (value.style_code === null || ['delicate', 'balanced', 'bold'].includes(String(value.style_code)));
}

function validWristState(value: unknown, wrist: WristInput | null): value is WristState | null {
  if (wrist === null) return value === null;
  if (!isObject(value) || !hasExactKeys(value, ['normalized_um', 'confirmed'])) return false;
  const parsed = parseWristToUm(wrist.value, wrist.unit);
  return parsed.ok && Number.isSafeInteger(value.normalized_um) && value.normalized_um === parsed.normalizedUm
    && typeof value.confirmed === 'boolean';
}

export function validateSnapshot(value: unknown, allowedInlineIds: ReadonlySet<string>, allowedAttachmentIds: ReadonlySet<string>): value is Snapshot {
  if (!isObject(value)) return false;
  const isLegacy = hasExactKeys(value, ['design', 'selectedInlineUid', 'nextUid']);
  const hasWristState = hasExactKeys(value, ['design', 'selectedInlineUid', 'nextUid', 'wristState']);
  const hasGiftMessage = hasExactKeys(value, ['design', 'selectedInlineUid', 'nextUid', 'wristState', 'giftMessage']);
  if (!isLegacy && !hasWristState && !hasGiftMessage) return false;
  if (hasGiftMessage && (typeof value.giftMessage !== 'string' || [...value.giftMessage].length > MAX_GIFT_MESSAGE || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value.giftMessage))) return false;
  const design = value.design;
  if (!isObject(design)) return false;
  const designKeys = [
    'schema_version', 'recipient_mode', 'intentions', 'zodiac_sign', 'birth_month', 'occasion_code',
    'inline_items', 'attachments', 'wrist', 'fit_preference', 'packaging_option_id', 'private_message_ref',
  ];
  const hasRecommendationContext = hasExactKeys(design, [...designKeys, 'recommendation_context']);
  if (!hasExactKeys(design, designKeys) && !hasRecommendationContext) return false;
  if (hasRecommendationContext && !validRecommendationContext(design.recommendation_context)) return false;
  if (design.schema_version !== '1.0' || (design.recipient_mode !== 'self' && design.recipient_mode !== 'gift') || design.occasion_code !== 'none') return false;
  if (design.recipient_mode === 'gift' && !hasRecommendationContext) return false;
  if (!Array.isArray(design.intentions) || design.intentions.length !== 0) return false;
  if (design.zodiac_sign !== null || design.birth_month !== null || design.private_message_ref !== null) return false;
  if (design.packaging_option_id !== null && design.packaging_option_id !== 'standard' && design.packaging_option_id !== 'gift_box') return false;
  if (design.wrist !== null && !validWrist(design.wrist)) return false;
  if (!validFitPreference(design.fit_preference)) return false;
  if ((design.wrist === null) !== (design.fit_preference === null)) return false;
  if (isLegacy) {
    if (design.wrist !== null) return false;
  } else if (!validWristState(value.wristState, design.wrist)) return false;
  if (!Array.isArray(design.inline_items) || design.inline_items.length > MAX_INLINE_ITEMS || !design.inline_items.every((item) => validInline(item, allowedInlineIds))) return false;
  if (!Array.isArray(design.attachments) || design.attachments.length > MAX_ATTACHMENTS || !design.attachments.every((item) => validAttachment(item, allowedAttachmentIds))) return false;
  const allUids = [...design.inline_items, ...design.attachments].map((item) => item.uid);
  if (new Set(allUids).size !== allUids.length) return false;
  const inlineUids = new Set(design.inline_items.map((item) => item.uid));
  if (design.attachments.some((item) => !inlineUids.has(item.anchor_uid))) return false;
  if (value.selectedInlineUid !== null && (typeof value.selectedInlineUid !== 'string' || !inlineUids.has(value.selectedInlineUid))) return false;
  if (!Number.isSafeInteger(value.nextUid) || Number(value.nextUid) <= 0) return false;
  let highestGeneratedSequence = 0;
  for (const uid of allUids) {
    const match = /^(?:bead|charm)-([0-9a-z]+)$/.exec(uid);
    if (!match) continue;
    const sequence = Number.parseInt(match[1], 36);
    if (!Number.isSafeInteger(sequence)) return false;
    highestGeneratedSequence = Math.max(highestGeneratedSequence, sequence);
  }
  return Number(value.nextUid) > highestGeneratedSequence;
}

export function resolveStorage(getStorage: () => StorageLike): StorageLike | null {
  try { return getStorage(); }
  catch { return null; }
}

export function resolveDraftLock(getLockManager: () => DraftLockManager): DraftLockManager | null {
  try {
    const manager = getLockManager();
    return manager && typeof manager.request === 'function' ? manager : null;
  } catch { return null; }
}

export async function withDraftLock<T>(
  lockManager: DraftLockManager | null,
  operation: () => T | Promise<T>,
  lockName = DRAFT_LOCK_NAME,
): Promise<{ ok: true; value: T } | { ok: false; message: string }> {
  if (!lockManager) return { ok: false, message: 'Reliable same-origin draft locking is unavailable. Changes remain in this tab but automatic saving is paused.' };
  try {
    const value = await lockManager.request(lockName, { mode: 'exclusive' }, operation);
    return { ok: true, value };
  } catch {
    return { ok: false, message: 'The browser draft lock could not be acquired. Changes remain in this tab but automatic saving is paused.' };
  }
}

export function loadDraft(
  storage: StorageLike | null,
  allowedInlineIds: ReadonlySet<string>,
  allowedAttachmentIds: ReadonlySet<string>,
  storageKey = STORAGE_KEY,
): DraftLoadResult {
  const empty = createEmptySnapshot();
  if (!storage) return { status: 'unavailable', snapshot: empty, message: 'Local browser storage is unavailable. You can still edit during this tab session.', revision: null, writerId: null };
  let raw: string | null;
  try { raw = storage.getItem(storageKey); }
  catch { return { status: 'unavailable', snapshot: empty, message: 'Local browser storage is unavailable. You can still edit during this tab session.', revision: null, writerId: null }; }
  if (raw === null) return { status: 'empty', snapshot: empty, revision: 0, writerId: null };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isObject(parsed) || parsed.storage_version !== STORAGE_VERSION || typeof parsed.saved_at !== 'string' || !validateSnapshot(parsed.snapshot, allowedInlineIds, allowedAttachmentIds)) {
      return { status: 'invalid', snapshot: empty, message: 'This saved draft is incompatible or damaged. Reset it before saving a new draft.', revision: null, writerId: null };
    }
    if (hasExactKeys(parsed, ['storage_version', 'saved_at', 'snapshot'])) {
      return { status: 'loaded', snapshot: cloneSnapshot(parsed.snapshot), savedAt: parsed.saved_at, revision: 0, writerId: null };
    }
    if (!hasExactKeys(parsed, ['storage_version', 'saved_at', 'revision', 'writer_id', 'snapshot'])
      || !Number.isSafeInteger(parsed.revision) || Number(parsed.revision) < 1
      || typeof parsed.writer_id !== 'string' || !WRITER_PATTERN.test(parsed.writer_id)) {
      return { status: 'invalid', snapshot: empty, message: 'This saved draft is incompatible or damaged. Reset it before saving a new draft.', revision: null, writerId: null };
    }
    return {
      status: 'loaded', snapshot: cloneSnapshot(parsed.snapshot), savedAt: parsed.saved_at,
      revision: Number(parsed.revision), writerId: parsed.writer_id,
    };
  } catch {
    return { status: 'invalid', snapshot: empty, message: 'This saved draft is incompatible or damaged. Reset it before saving a new draft.', revision: null, writerId: null };
  }
}

export function saveDraft(
  storage: StorageLike | null,
  snapshot: Snapshot,
  expectedRevision: number,
  writerId: string,
  allowedInlineIds: ReadonlySet<string>,
  allowedAttachmentIds: ReadonlySet<string>,
  storageKey = STORAGE_KEY,
): DraftWriteResult {
  const savedAt = new Date().toISOString();
  if (!storage) return { ok: false, kind: 'unavailable', message: 'Local browser storage is unavailable. Your edits will remain only in this tab.', actualRevision: null };
  if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 0 || !WRITER_PATTERN.test(writerId)
    || !validateSnapshot(snapshot, allowedInlineIds, allowedAttachmentIds)) {
    return { ok: false, kind: 'invalid', message: 'This draft could not be validated for safe local saving.', actualRevision: null };
  }
  const current = loadDraft(storage, allowedInlineIds, allowedAttachmentIds, storageKey);
  if (current.status === 'unavailable') return { ok: false, kind: 'unavailable', message: current.message, actualRevision: null };
  if (current.status === 'invalid') return { ok: false, kind: 'invalid', message: current.message, actualRevision: null };
  if (current.revision !== expectedRevision) {
    return {
      ok: false, kind: 'conflict', actualRevision: current.revision,
      message: 'Another window saved a newer browser draft. This page was kept unchanged and automatic saving is paused.',
    };
  }
  const revision = expectedRevision + 1;
  try {
    storage.setItem(storageKey, JSON.stringify({
      storage_version: STORAGE_VERSION, saved_at: savedAt, revision, writer_id: writerId, snapshot,
    }));
    return { ok: true, savedAt, revision };
  } catch {
    return { ok: false, kind: 'unavailable', message: 'Local browser storage is unavailable. Your edits will remain only in this tab.', actualRevision: null };
  }
}

export function clearDraft(
  storage: StorageLike | null,
  expectedRevision: number | null,
  writerId: string,
  allowedInlineIds: ReadonlySet<string>,
  allowedAttachmentIds: ReadonlySet<string>,
  storageKey = STORAGE_KEY,
): DraftClearResult {
  if (!storage) return { ok: false, kind: 'unavailable', message: 'Local browser storage is unavailable. The saved draft was not cleared.', actualRevision: null };
  if (!WRITER_PATTERN.test(writerId)) return { ok: false, kind: 'unavailable', message: 'The browser draft reset could not be validated.', actualRevision: null };
  const current = loadDraft(storage, allowedInlineIds, allowedAttachmentIds, storageKey);
  if (current.status === 'unavailable') return { ok: false, kind: 'unavailable', message: current.message, actualRevision: null };
  const matchesInvalidRecovery = expectedRevision === null && current.status === 'invalid';
  const matchesRevision = expectedRevision !== null && current.status !== 'invalid' && current.revision === expectedRevision;
  if (!matchesInvalidRecovery && !matchesRevision) {
    return {
      ok: false, kind: 'conflict', actualRevision: current.status === 'invalid' ? null : current.revision,
      message: 'Another window changed the browser draft. The newer saved draft was not reset.',
    };
  }
  const revision = current.status === 'invalid' ? 1 : current.revision + 1;
  const savedAt = new Date().toISOString();
  try {
    storage.setItem(storageKey, JSON.stringify({
      storage_version: STORAGE_VERSION,
      saved_at: savedAt,
      revision,
      writer_id: writerId,
      snapshot: createEmptySnapshot(),
    }));
    return { ok: true, savedAt, revision };
  }
  catch { return { ok: false, kind: 'unavailable', message: 'Local browser storage is unavailable. The saved draft was not cleared.', actualRevision: null }; }
}
